/**
 * ToolTestPage - 공통 학습 도구 테스트 페이지 컴포넌트
 *
 * 개발/테스트 환경에서 QueBoard 등 학습 도구를 테스트하는 페이지.
 * Admin 목업 역할: 문제 설정, 기능 토글, 튜터리얼 설정.
 */
import { useMemo, useState } from 'react'
import { QueBoard } from './QueBoard'
import { DEFAULT_FEATURES, resolveTutorialModules, ALL_TUTORIAL_MODULES } from './QueBoard'
import { FEATURED_PROBLEMS, DOT_COLORS, findCurriculumEntry, entryToFeatures } from './QueBoard'
import type { CharacterId, QueBoardConfig, QueBoardFeatures, TutorialModuleId } from './QueBoard'

export interface ToolTestPageProps {
  /** 현재 미사용. 추후 i18n 확장용 */
  t?: (key: string) => string
}


export function ToolTestPage({ t: _t }: ToolTestPageProps) {
  const [activeTool, setActiveTool] = useState<string | null>(null)
  const [completedLog, setCompletedLog] = useState<string[]>([])

  // --- Config state ---
  const [problemLeft, setProblemLeft] = useState(3)
  const [problemRight, setProblemRight] = useState(5)
  const allowCustomInput = false
  const [features, setFeatures] = useState<QueBoardFeatures>({ ...DEFAULT_FEATURES })
  const [tutorialEnabled, setTutorialEnabled] = useState(false)
  const tutorialAutoStart = true
  const [selectedModule, setSelectedModule] = useState<TutorialModuleId | null>(null)

  const [character, setCharacter] = useState<CharacterId>('ddami')

  const [problemView, setProblemView] = useState<'matrix' | 'category'>('category')

  // Config key for forcing remount when config changes
  const [configKey, setConfigKey] = useState(0)

  const toggleFeature = (key: keyof QueBoardFeatures) => {
    setFeatures(prev => ({ ...prev, [key]: !prev[key] }))
  }

  const selectProblem = (left: number, right: number, feat?: Partial<QueBoardFeatures>) => {
    setProblemLeft(left)
    setProblemRight(right)
    if (feat) {
      setFeatures(prev => ({ ...prev, ...feat }))
    } else {
      const entry = findCurriculumEntry(left, right)
      if (entry) setFeatures(entryToFeatures(entry))
    }
    setConfigKey(prev => prev + 1)
  }

  const selectedModules = useMemo(() => selectedModule ? [selectedModule] : [], [selectedModule])

  const config = useMemo<QueBoardConfig>(() => ({
    problem: { left: problemLeft, right: problemRight },
    allowCustomInput,
    features,
    tutorial: {
      enabled: tutorialEnabled,
      modules: selectedModules,
      autoStart: tutorialAutoStart,
    },
    character,
  }), [problemLeft, problemRight, allowCustomInput, features, tutorialEnabled, selectedModules, tutorialAutoStart, character])

  const tutorialModules = useMemo(
    () => resolveTutorialModules(selectedModules, { left: problemLeft, right: problemRight }),
    [selectedModules, problemLeft, problemRight],
  )

  const applyConfig = () => setConfigKey(prev => prev + 1)

  // 구구단 매트릭스 범위
  const RANGE = [2, 3, 4, 5, 6, 7, 8, 9]

  return (
    <div className="p-6 mx-auto space-y-6 max-w-5xl">
      <h1 className="text-2xl font-bold text-gray-900">Tool Test Page</h1>
      <p className="text-sm text-gray-600">
        학습 도구를 테스트할 수 있습니다. 개발 환경에서만 사용 가능합니다.
      </p>

      {/* Tool selection */}
      <div className="p-6 bg-white rounded-lg border border-gray-200 shadow-2xs space-y-4">
        <h2 className="text-lg font-semibold text-gray-800">도구 선택</h2>
        <div className="flex flex-wrap gap-3">
          <button
            onClick={() => setActiveTool(activeTool === 'queboard' ? null : 'queboard')}
            className={`px-4 py-2 rounded-lg border font-medium transition-colors ${
              activeTool === 'queboard'
                ? 'bg-indigo-600 text-white border-indigo-600'
                : 'bg-white text-gray-700 border-gray-300 hover:border-indigo-400 hover:bg-indigo-50'
            }`}
          >
            깨보드 (QueBoard)
          </button>
        </div>
      </div>

      {/* QueBoard test */}
      {activeTool === 'queboard' && (
        <div className="space-y-4">
          {/* Config panel */}
          <div className="p-6 bg-white rounded-lg border border-gray-200 shadow-2xs space-y-5">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-800">설정 (Admin 목업)</h2>
              <div className="flex items-center gap-2 text-sm">
                <span className="text-gray-500">현재 문제:</span>
                <input
                  type="number" min={1} max={10} value={problemLeft}
                  onChange={e => setProblemLeft(Math.min(10, Math.max(1, Number(e.target.value))))}
                  className="w-12 px-1.5 py-1 text-sm border border-gray-300 rounded-xs text-center"
                />
                <span className="font-bold text-gray-600">×</span>
                <input
                  type="number" min={1} max={10} value={problemRight}
                  onChange={e => setProblemRight(Math.min(10, Math.max(1, Number(e.target.value))))}
                  className="w-12 px-1.5 py-1 text-sm border border-gray-300 rounded-xs text-center"
                />
                <span className="text-gray-400">= {problemLeft * problemRight}</span>
              </div>
            </div>

            {/* 문제 선택 탭 */}
            <div className="space-y-2">
              <div className="flex items-center gap-3">
                <div className="flex border border-gray-300 rounded-md overflow-hidden text-xs">
                  <button
                    onClick={() => setProblemView('matrix')}
                    className={`px-3 py-1.5 font-medium transition-colors ${
                      problemView === 'matrix' ? 'bg-indigo-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'
                    }`}
                  >
                    구구단 매트릭스
                  </button>
                  <button
                    onClick={() => setProblemView('category')}
                    className={`px-3 py-1.5 font-medium transition-colors ${
                      problemView === 'category' ? 'bg-indigo-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'
                    }`}
                  >
                    기능별 문제
                  </button>
                </div>
                <div className="flex gap-2">
                  {DOT_COLORS.map(d => (
                    <span key={d.key} className="flex items-center gap-1 text-xs text-gray-500">
                      <span className={`inline-block w-2 h-2 rounded-full ${d.color}`} />
                      {d.label}
                    </span>
                  ))}
                </div>
              </div>
              {problemView === 'matrix' && <div className="overflow-x-auto">
                <table className="border-collapse text-xs">
                  <thead>
                    <tr>
                      <th className="w-8 h-8 text-gray-400 font-normal">×</th>
                      {RANGE.map(c => (
                        <th key={c} className="w-14 h-8 text-center text-gray-500 font-semibold">{c}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {RANGE.map(r => (
                      <tr key={r}>
                        <td className="text-center text-gray-500 font-semibold">{r}</td>
                        {RANGE.map(c => {
                          const entry = findCurriculumEntry(r, c)
                          const isActive = problemLeft === r && problemRight === c
                          const hasAny = entry && (entry.teleport10 || entry.teleport5 || entry.fatTie || entry.bank)
                          return (
                            <td key={c} className="p-0.5">
                              <button
                                onClick={() => selectProblem(r, c)}
                                className={`w-full h-10 rounded border text-center transition-all ${
                                  isActive
                                    ? 'bg-indigo-100 border-indigo-500 ring-2 ring-indigo-300'
                                    : hasAny
                                      ? 'bg-white border-gray-200 hover:border-gray-400 hover:bg-gray-50'
                                      : 'bg-gray-50 border-gray-100 text-gray-400 hover:bg-gray-100'
                                }`}
                              >
                                <div className="text-[10px] font-medium leading-tight">{r * c}</div>
                                {entry && hasAny && (
                                  <div className="flex justify-center gap-0.5 mt-0.5">
                                    {DOT_COLORS.map(d => (
                                      entry[d.key] ? (
                                        <span key={d.key} className={`inline-block w-1.5 h-1.5 rounded-full ${d.color}`} />
                                      ) : null
                                    ))}
                                  </div>
                                )}
                              </button>
                            </td>
                          )
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>}
              {problemView === 'category' && <div className="space-y-3">
              {FEATURED_PROBLEMS.map((cat) => (
                <div key={cat.category} className="space-y-1.5">
                  <div className="flex items-center gap-2">
                    <span className={`inline-block w-2 h-2 rounded-full ${cat.dotColor}`} />
                    <span className="text-xs font-semibold text-gray-600">{cat.category}</span>
                    <span className="text-xs text-gray-400">{cat.description}</span>
                    <span className="text-xs text-gray-300">({cat.problems.length})</span>
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {cat.problems.map((p) => {
                      const isActive = problemLeft === p.left && problemRight === p.right
                      return (
                        <button
                          key={`${cat.category}-${p.left}x${p.right}`}
                          onClick={() => selectProblem(p.left, p.right)}
                          className={`px-2.5 py-1 text-xs rounded-md border transition-colors ${
                            isActive ? cat.activeColor : `bg-white ${cat.color}`
                          }`}
                        >
                          {p.left} × {p.right}
                        </button>
                      )
                    })}
                  </div>
                </div>
              ))}
            </div>}
            </div>

            {/* Features */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700">기능 토글</label>
              <div className="flex flex-wrap gap-4">
                {([
                  ['tie', '묶기'],
                  ['teleport5', '텔레포트(5)'],
                  ['teleport10', '텔레포트(10)'],
                  ['bank', '은행'],
                  ['indicator', '표시등'],
                  ['customInput', '문제 커스텀'],
                ] as [keyof QueBoardFeatures, string][]).map(([key, label]) => (
                  <label key={key} className="flex items-center gap-1.5 text-sm text-gray-700">
                    <input
                      type="checkbox" checked={features[key]}
                      onChange={() => toggleFeature(key)}
                      className="rounded-xs"
                    />
                    {label}
                  </label>
                ))}
              </div>
            </div>

            {/* Character */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700">캐릭터</label>
              <div className="flex flex-wrap gap-4">
                {([
                  ['queda', '깨다'],
                  ['chaeli', '챌리'],
                  ['ddami', '따미'],
                  ['random', '랜덤'],
                ] as [CharacterId, string][]).map(([id, label]) => (
                  <label key={id} className="flex items-center gap-1.5 text-sm text-gray-700">
                    <input
                      type="radio" name="character"
                      checked={character === id}
                      onChange={() => { setCharacter(id); setConfigKey(prev => prev + 1) }}
                    />
                    {label}
                  </label>
                ))}
              </div>
            </div>

            {/* Tutorial */}
            <div className="space-y-2">
              <div className="flex items-center gap-3">
                <label className="text-sm font-medium text-gray-700">튜터리얼</label>
                <label className="flex items-center gap-1.5 text-sm text-gray-600">
                  <input
                    type="checkbox" checked={tutorialEnabled}
                    onChange={() => setTutorialEnabled(!tutorialEnabled)}
                    className="rounded-xs"
                  />
                  활성화
                </label>
              </div>
              {tutorialEnabled && (
                <div className="flex flex-wrap gap-3">
                  {ALL_TUTORIAL_MODULES.map((m) => (
                    <label
                      key={m.id}
                      className={`flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-md border cursor-pointer transition-colors ${
                        selectedModule === m.id
                          ? 'bg-purple-50 border-purple-400 text-purple-800'
                          : 'bg-white border-gray-300 text-gray-600 hover:border-purple-300'
                      }`}
                    >
                      <input
                        type="radio"
                        name="tutorial-module"
                        checked={selectedModule === m.id}
                        onChange={() => {
                          setSelectedModule(selectedModule === m.id ? null : m.id)
                          setConfigKey(prev => prev + 1)
                        }}
                        className="text-purple-600"
                      />
                      {m.name}
                      <span className="text-xs text-gray-400 ml-1">({m.description})</span>
                    </label>
                  ))}
                </div>
              )}
            </div>

            {/* Apply button */}
            <button
              onClick={applyConfig}
              className="px-4 py-2 bg-indigo-600 text-white rounded-lg font-medium hover:bg-indigo-700 transition-colors"
            >
              설정 적용
            </button>
          </div>

          {/* QueBoard */}
          <div className="p-6 bg-white rounded-lg border border-gray-200 shadow-2xs space-y-4">
            <div className="flex justify-between items-center">
              <h2 className="text-lg font-semibold text-gray-800">깨보드 (QueBoard)</h2>
              <p className="text-sm text-gray-500">
                {problemLeft} × {problemRight} = {problemLeft * problemRight}
              </p>
            </div>

            <div className="pt-2 border-t border-gray-200">
              <QueBoard
                key={configKey}
                isOpen={true}
                onClose={() => setActiveTool(null)}
                config={config}
                tutorialModules={tutorialModules}
                onComplete={(result) => {
                  const msg = `${result.problem[0]} × ${result.problem[1]} = ${result.answer} (정답!)`
                  setCompletedLog((prev) => [msg, ...prev])
                }}
              />
            </div>

            {/* Completion log */}
            {completedLog.length > 0 && (
              <div className="pt-4 border-t border-gray-200 space-y-2">
                <div className="flex justify-between items-center">
                  <h3 className="text-sm font-semibold text-gray-700">완료 기록</h3>
                  <button
                    onClick={() => setCompletedLog([])}
                    className="text-xs text-gray-500 hover:text-gray-700"
                  >
                    기록 삭제
                  </button>
                </div>
                <div className="space-y-1">
                  {completedLog.map((log, i) => (
                    <div key={i} className="px-3 py-1.5 text-sm text-green-700 bg-green-50 rounded-xs">
                      {log}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
